export interface DownloadItem {
  /** Spec row label, e.g. "Compatibility". */
  label: string;
  /** Spec row value, e.g. "Android 7.1 and above". */
  value: string;
  versions: {
    label: string;
    version: string;
    downloadUrl: string;
  }[];
}

export interface DownloadPlatform {
  id: string;
  name: string;
  icon: string;
  description: string;
  installationGuideUrl: string;
  /** Brand colour for the download button. Falls back to the site accent. */
  brandColor?: string;
  appStoreLink?: {
    url: string;
    icon: string;
    alt: string;
    /** Override badge dimensions when the default 120×30 is too narrow. */
    iconWidth?: number;
    iconHeight?: number;
    iconClassName?: string;
  };
  downloadItems: DownloadItem[];
}

export const downloadPlatforms: DownloadPlatform[] = [
  {
    id: "android",
    name: "Android",
    icon: "android-icon.T_cWAiALEYFE.svg",
    description:
      "Download and install Pickcel Android apk for your Android devices (signage media players, tablet, phone, Smart TVs).",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/android/apk-download",
    brandColor: "#4C8C2A",
    appStoreLink: {
      url: "https://play.google.com/store/apps/details?id=pickcel.digital.signage&hl=en",
      icon: "play-store.3feJonls4P5A.svg",
      alt: "play-store",
      iconWidth: 150,
      iconHeight: 32,
      iconClassName: "h-[32px] w-auto max-w-none object-contain",
    },
    downloadItems: [
      {
        label: "Compatibility",
        value: "Android 7.1 and above",
        versions: [
          {
            label: "Latest version",
            version: "v7.2.0",
            downloadUrl: "https://console.pickcel.com/players/android/digital-player-v7.2.0.apk",
          },
        ],
      },
    ],
  },
  {
    id: "windows",
    name: "Windows",
    icon: "windows-icon.O3EucuHJbOIO.svg",
    description:
      "Download and install Pickcel windows EXE file on your Windows devices (signage media players, Laptops, thin clients).",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/windows",
    brandColor: "#0F7BE8",
    appStoreLink: {
      url: "https://apps.microsoft.com/store/detail/9n5bf0bg9m73?hl=en-in&gl=in",
      icon: "microsoft-apps.t32BKAWNyydk.svg",
      alt: "microsoft",
    },
    downloadItems: [
      {
        label: "Compatibility",
        value: "Windows 10 & 11",
        versions: [
          {
            label: "Latest version",
            version: "4.2.0",
            downloadUrl: "https://console.pickcel.com/players/core/windows/pickcel-digital-signage-v4.2.0.exe",
          },
        ],
      },
    ],
  },
  {
    id: "brightsign",
    name: "BrightSign",
    icon: "brightsign-icon.GXcscxV_PIFl.svg",
    description:
      "Pickcel integrates smoothly with BrightSign LS425 and XT1144, ensuring pro-grade signage performance.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/brightsign",
    brandColor: "#4C1D95",
    downloadItems: [
      {
        label: "Supported models",
        value: "XT1144, LS425",
        versions: [
          {
            label: "Latest version",
            version: "1.0.3",
            downloadUrl: "https://console.pickcel.com/players/core/brightsign/autorun.zip",
          },
        ],
      },
    ],
  },
  {
    id: "raspberry-pi",
    name: "Raspberry pi",
    icon: "rasberrypi-icon.qgc48pww-gRb.svg",
    description: "Download and install the Pickcel package for your Raspberry Pi devices.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/raspberry-pi",
    brandColor: "#C51A4A",
    downloadItems: [
      {
        label: "Compatibility",
        value: "Raspberry Pi 4.0 and above",
        versions: [
          {
            label: "Latest version",
            version: "4.0.3",
            downloadUrl: "https://console.pickcel.com/players/raspberrypi/v4/4.0.3/pickcel.img.gz",
          },
        ],
      },
    ],
  },
  {
    id: "lg-webos",
    name: "LG WebOS",
    icon: "lgwebos-icon.c0W98CsCT_9h.svg",
    description: "Download and install the Pickcel application for your LG WebOS commercial displays.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/lg-webos/url-method",
    brandColor: "#A50034",
    downloadItems: [
      {
        label: "Compatibility",
        value: "webOS 4 and above",
        versions: [
          {
            label: "Latest version",
            version: "5.0.4",
            downloadUrl: "https://console.pickcel.com/players/core/lg/com.lg.app.signage.ipk",
          },
        ],
      },
    ],
  },
  {
    id: "chrome-os",
    name: "Chrome OS",
    icon: "chrome-icon.o-_Jvbswkdnn.svg",
    description:
      'Pickcel Digital Signage is supported on Chrome OS devices like Chromebit or Chromebox.To get this app, access Chrome Webstore from your Chrome device and search for "Pickcel Digital Signage.',
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/chrome-os-flex",
    brandColor: "#1A73E8",
    appStoreLink: {
      url: "https://chromewebstore.google.com/detail/pickcel-player/pllfmbcgfjgapdmnmnlalalnpmmocopc",
      icon: "chrome-store.zQ8ac4C_0Csh.svg",
      alt: "chrome-web-store",
      iconWidth: 150,
      iconHeight: 32,
      iconClassName: "h-[32px] w-auto max-w-none object-contain",
    },
    downloadItems: [],
  },
  {
    id: "samsung-tizen",
    name: "Samsung",
    icon: "samsung-icon.4i2f3tx2ckKV.svg",
    description:
      "Pickcel Digital Signage is supported on the Tizen OS of Samsung Smart TVs. To get this app, please follow the steps mentioned in installation guide.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/samsung-tizen",
    brandColor: "#1428A0",
    downloadItems: [],
  },
  {
    id: "amazon-fire-tv-stick",
    name: "Amazon Fire TV Stick",
    icon: "amazon-icon.Th0Xhxly9QOr.svg",
    description:
      'Pickcel digital signage is supported on Amazon Fire TV Stick and Fire TV Box. To install Pickcel digital signage app, text or voice search for "Pickcel Digital Signage" on your Amazon Fire TV device.',
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/amazon-fire-tv-stick",
    brandColor: "#E47911",
    appStoreLink: {
      url: "https://www.amazon.in/Lanesquare-Technology-Private-Limited-Pickcel/dp/B07GBC1W29/",
      icon: "amazon-webstore.3PZtpMD3cBiR.svg",
      alt: "amazon-webstore",
      iconWidth: 150,
      iconHeight: 32,
      iconClassName: "h-[32px] w-auto max-w-none object-contain",
    },
    downloadItems: [],
  },
  // {
  //   id: "macos",
  //   name: "MacOS",
  //   icon: "macOS-icon.WHpXn-I5AbhN.svg",
  //   description: "Supported on MacOS machines. Download DMG file and install on your MAC machine.",
  //   installationGuideUrl:
  //     "https://docs.pickcel.com/player-installation/macos",
  //   downloadItems: [
  //     {
  //       title: "MacOS",
  //       description: "Mac device/machine",
  //       versions: [
  //         {
  //           label: "Latest version",
  //           version: "1.0.0",
  //           downloadUrl: "https://signage.pickcel.com/players/pickcel-mac-player-1.0.0.dmg",
  //         },
  //       ],
  //     },
  //   ],
  // },
  {
    id: "linux",
    name: "Linux",
    icon: "linux-icon.N0ksN9mZqEEr.svg",
    description: "Download and install the Pickcel package for your Linux devices running Ubuntu OS.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/linux",
    brandColor: "#DD4814",
    downloadItems: [
      {
        label: "Compatibility",
        value: "Intel® NUC, Ubuntu 18 and above",
        versions: [
          {
            label: "Latest version",
            version: "3.5.6",
            downloadUrl: "https://console.pickcel.com/players/core/linux/pickcel-digital-signage-v3.5.6.deb",
          },
        ],
      },
    ],
  },
  {
    id: "iOS",
    name: "iOS",
    icon: "iOS-icon.C47yeGfmaNKq.svg",
    description:
      "Download and install the Pickcel iOS app on your Apple devices (such as iPhones and iPads) from the App Store.",
    installationGuideUrl:
      "https://docs.pickcel.com/player-installation/ios",
    brandColor: "#111827",
    appStoreLink: {
      url: "https://apps.apple.com/in/app/pickcel-go-digital-signage/id1533647872",
      icon: "app_store_iOS_without_outline.bPCC-e5JQk5Y.svg",
      alt: "app-store",
    },
    downloadItems: [],
  },
];

